import type { z } from "zod";
import { AsyncQueue } from "@/shared/async-queue";
import { newId } from "@/shared/prefixed-id";

export type VADChunk = { type: "result"; chunk: Int16Array; score: number };

export type VADJob = {
  id: string;
  cancel: () => void;
  stream: AsyncQueue<VADChunk>;
  inputVoice: (pcm: Int16Array) => void;
  _abortController: AbortController;
};

export abstract class VADBase<ConfigSchema extends z.ZodObject> {
  protected config: z.infer<ConfigSchema>;

  constructor(configSchema: ConfigSchema, config: Partial<z.infer<ConfigSchema>>) {
    this.config = configSchema.parse({ ...config });
  }

  protected createGenerateJob(): VADJob {
    const id = newId("job");
    const stream = new AsyncQueue<VADChunk>();
    const _abortController = new AbortController();
    const job: VADJob = {
      id,
      cancel: () => _abortController.abort(),
      stream,
      inputVoice: (pcm: Int16Array) => this.onVoiceInput(job, pcm),
      _abortController,
    };
    return job;
  }

  /**
   * Create a new job to detect voice activity of an incomoing audio stream.
   */
  abstract detect(): Promise<VADJob>;
  protected abstract onVoiceInput(job: VADJob, pcm: Int16Array): Promise<void>;
}
